// src/api/threadCollaborationApi.ts

import { baseApi } from './baseApi';
import type {
  ThreadParticipant,
  UserThreadPreference,
  PinnedMessage,
  CollaborationSignalType,
} from '../types/api';

export const threadCollaborationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // =============================================
    // Thread Participants
    // =============================================
    getThreadParticipants: builder.query<
      ThreadParticipant[],
      { threadId: string; workspaceId: string }
    >({
      query: ({ threadId, workspaceId }) => ({
        url: `/threads/${threadId}/participants`,
        params: { workspaceId },
      }),
      providesTags: (result, error, { threadId }) => [
        { type: 'ThreadParticipant', id: threadId },
      ],
    }),

    addThreadParticipant: builder.mutation<
      ThreadParticipant,
      { threadId: string; workspaceId: string; userId: string; role?: string }
    >({
      query: ({ threadId, workspaceId, userId, role }) => ({
        url: `/threads/${threadId}/participants`,
        method: 'POST',
        params: { workspaceId },
        body: { userId, role },
      }),
      invalidatesTags: (result, error, { threadId }) => [
        { type: 'ThreadParticipant', id: threadId },
      ],
    }),

    removeThreadParticipant: builder.mutation<
      { success: boolean },
      { threadId: string; participantId: string; workspaceId: string }
    >({
      query: ({ threadId, participantId, workspaceId }) => ({
        url: `/threads/${threadId}/participants/${participantId}`,
        method: 'DELETE',
        params: { workspaceId },
      }),
      invalidatesTags: (result, error, { threadId }) => [
        { type: 'ThreadParticipant', id: threadId },
      ],
    }),

    updateLastSeen: builder.mutation<
      { success: boolean; lastSeenAt: string },
      { threadId: string; workspaceId: string }
    >({
      query: ({ threadId, workspaceId }) => ({
        url: `/threads/${threadId}/participants/seen`,
        method: 'PATCH',
        params: { workspaceId },
      }),
    }),

    // =============================================
    // Thread Preferences (Mute/Follow)
    // =============================================
    getThreadPreferences: builder.query<
      UserThreadPreference,
      { threadId: string; workspaceId: string }
    >({
      query: ({ threadId, workspaceId }) => ({
        url: `/threads/${threadId}/preferences`,
        params: { workspaceId },
      }),
      providesTags: (result, error, { threadId }) => [
        { type: 'ThreadPreference', id: threadId },
      ],
    }),

    updateThreadPreferences: builder.mutation<
      UserThreadPreference,
      {
        threadId: string;
        workspaceId: string;
        isMuted?: boolean;
        isFollowing?: boolean;
        mutedUntil?: string | null;
      }
    >({
      query: ({ threadId, workspaceId, ...body }) => ({
        url: `/threads/${threadId}/preferences`,
        method: 'PATCH',
        params: { workspaceId },
        body,
      }),
      invalidatesTags: (result, error, { threadId }) => [
        { type: 'ThreadPreference', id: threadId },
      ],
    }),

    // =============================================
    // Pinned Messages
    // =============================================
    getPinnedMessages: builder.query<
      PinnedMessage[],
      { threadId: string; workspaceId: string }
    >({
      query: ({ threadId, workspaceId }) => ({
        url: `/threads/${threadId}/pins`,
        params: { workspaceId },
      }),
      providesTags: (result, error, { threadId }) => [
        { type: 'PinnedMessage', id: threadId },
      ],
    }),

    pinMessage: builder.mutation<
      PinnedMessage,
      {
        messageId: string;
        workspaceId: string;
        duration?: '1h' | '1d' | '1w' | 'forever';
      }
    >({
      query: ({ messageId, workspaceId, duration }) => ({
        url: `/messages/${messageId}/pin`,
        method: 'POST',
        params: { workspaceId },
        body: { duration },
      }),
      invalidatesTags: ['PinnedMessage'],
    }),

    unpinMessage: builder.mutation<
      { success: boolean },
      { messageId: string; workspaceId: string }
    >({
      query: ({ messageId, workspaceId }) => ({
        url: `/messages/${messageId}/pin`,
        method: 'DELETE',
        params: { workspaceId },
      }),
      invalidatesTags: ['PinnedMessage'],
    }),

    // =============================================
    // Collaboration Signals
    // =============================================
    emitCollaborationSignal: builder.mutation<
      { success: boolean },
      {
        threadId: string;
        workspaceId: string;
        signalType: CollaborationSignalType;
        metadata?: Record<string, unknown>;
      }
    >({
      query: ({ threadId, workspaceId, signalType, metadata }) => ({
        url: `/threads/${threadId}/signals`,
        method: 'POST',
        params: { workspaceId },
        body: { signalType, metadata },
      }),
    }),
  }),
});

export const {
  useGetThreadParticipantsQuery,
  useAddThreadParticipantMutation,
  useRemoveThreadParticipantMutation,
  useUpdateLastSeenMutation,
  useGetThreadPreferencesQuery,
  useUpdateThreadPreferencesMutation,
  useGetPinnedMessagesQuery,
  usePinMessageMutation,
  useUnpinMessageMutation,
  useEmitCollaborationSignalMutation,
} = threadCollaborationApi;
